// "Mensagens entre alunos" — peer chat Fase 2.
// [[project-peer-chat-floating-and-hub]] Fase 2: layout dedicado em duas
// colunas (lista de threads à esquerda, conversa à direita), tela cheia.
//
// Diferenças vs PeerChatDock (Fase 1):
//   - Conversa em tela cheia, não janelinha.
//   - Lista de threads visível ao lado.
//   - Busca por nome do peer no topo.
//   - Marca como lidas automaticamente quando seleciona a thread.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  Loader2,
  MessageCircle,
  MessageCirclePlus,
  PictureInPicture2,
  Search,
  Send,
  X
} from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { getInitials } from "@app/ui/lib/display-name";
import { openAlert } from "./ConfirmPromptModal";
import { openChatWith } from "./PeerChatDock";
import { prefs, savePrefs } from "./App";
import { parseShareBody, ShareBubble } from "./PeerChatShareBubble";

interface ThreadRow {
  id: string;
  peer: { id: string; name: string | null; email: string | null };
  lastMessage: { body: string; senderId: string | null; isMine: boolean } | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

interface ChatMessage {
  id: string;
  senderId: string;
  body: string;
  createdAt: string;
  isMine: boolean;
  readByRecipientAt: string | null;
}

const POLL_MS = 8000;

const threads = signal<ThreadRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
/** Thread selecionada no hub. Exportada pra que outros componentes
 *  (ex: PeerChatDock → botão "abrir no Chat") possam navegar com a
 *  conversa correta já aberta. */
export const activeThreadId = signal<string | null>(null);

/** Abre o PeerChat Hub com a thread já selecionada. Chamado do
 *  PeerChatDock quando o aluno clica em "Abrir no Chat" pra ir pra
 *  conversa em tela cheia com aquela pessoa. */
export function openPeerChatHubWithThread(threadId: string | null): void {
  if (threadId) activeThreadId.value = threadId;
}

async function loadThreads() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const res = await call<{ threads: ThreadRow[] }>(
      "/api/student/chat/threads"
    );
    threads.value = res.threads;
    if (!activeThreadId.value && res.threads.length > 0) {
      activeThreadId.value = res.threads[0].id;
    }
    phase.value = "ready";
  } catch (e) {
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

interface ConnectionRow {
  id: string;
  other: { userId: string; name: string; email: string };
  status: string;
}

export function PeerChatHubView() {
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    void loadThreads();
    const poll = setInterval(() => void loadThreads(), POLL_MS * 2);
    return () => clearInterval(poll);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads.value;
    return threads.value.filter((t) => {
      const hay =
        (t.peer.name ?? "").toLowerCase() +
        " " +
        (t.peer.email ?? "").toLowerCase() +
        " " +
        (t.lastMessage?.body ?? "").toLowerCase();
      return hay.includes(q);
    });
  }, [threads.value, search]);

  const active = threads.value.find((t) => t.id === activeThreadId.value) ?? null;

  return (
    <div class="space-y-3">
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <MessageCircle className="size-6 text-primary" />
            Chat
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Converse 1:1 com seus colegas conectados.
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <AutoOpenToggle />
          <button
            type="button"
            onClick={() => setShowNew(true)}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            <MessageCirclePlus className="size-4" />
            Nova conversa
          </button>
        </div>
      </header>

      <div class="flex h-[calc(100vh-14rem)] min-h-[28rem] gap-3 rounded-lg border border-border bg-card/40">
        {/* Coluna esquerda: lista de threads */}
        <aside class="flex w-72 shrink-0 flex-col border-r border-border">
          <div class="border-b border-border p-2">
            <div class="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                placeholder="Buscar pessoa ou mensagem…"
                aria-label="Buscar conversa"
                class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-7 text-xs outline-none focus:border-primary"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Limpar"
                  class="absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          </div>

          <ul class="flex-1 space-y-0.5 overflow-y-auto p-1">
            {phase.value === "loading" && (
              <li class="px-3 py-3 text-center text-xs text-muted-foreground">
                <Loader2 className="mr-1 inline size-3 animate-spin" /> Carregando…
              </li>
            )}
            {phase.value === "error" && (
              <li class="px-3 py-2 text-xs text-destructive">
                <AlertCircle className="mr-1 inline size-3" /> {errMsg.value}
              </li>
            )}
            {phase.value === "ready" && filtered.length === 0 && (
              <li class="px-3 py-6 text-center text-[11px] text-muted-foreground">
                {search
                  ? "Nenhuma conversa bate com a busca."
                  : "Você ainda não tem conversas. Vá em Conexões e clique em Conversar."}
              </li>
            )}
            {filtered.map((t) => (
              <ThreadItem
                key={t.id}
                thread={t}
                active={t.id === activeThreadId.value}
                onClick={() => (activeThreadId.value = t.id)}
              />
            ))}
          </ul>
        </aside>

        {/* Coluna direita: conversa */}
        <main class="flex min-w-0 flex-1 flex-col">
          {active ? (
            <ChatPane thread={active} onMessage={() => void loadThreads()} />
          ) : (
            <div class="grid flex-1 place-items-center text-center text-sm text-muted-foreground">
              <div>
                <MessageCircle className="mx-auto mb-2 size-8 text-muted-foreground/40" />
                Selecione uma conversa à esquerda.
              </div>
            </div>
          )}
        </main>
      </div>

      {showNew && (
        <NewChatModal
          onClose={() => setShowNew(false)}
          onOpened={(threadId) => {
            activeThreadId.value = threadId;
            setShowNew(false);
            void loadThreads();
          }}
        />
      )}
    </div>
  );
}

/** Modal: lista conexões aceitas. Clicar abre/cria thread + ativa. */
function NewChatModal({
  onClose,
  onOpened
}: {
  onClose: () => void;
  onOpened: (threadId: string) => void;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [conns, setConns] = useState<ConnectionRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await call<{ connections: ConnectionRow[] }>(
          "/api/student/connections?box=active"
        );
        if (!cancelled) {
          setConns(r.connections);
          setPhase("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setErr(friendlyError(e));
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return conns;
    return conns.filter(
      (c) =>
        c.other.name.toLowerCase().includes(needle) ||
        (c.other.email ?? "").toLowerCase().includes(needle)
    );
  }, [conns, q]);

  async function startChat(c: ConnectionRow) {
    if (opening) return;
    setOpening(c.other.userId);
    try {
      const r = await call<{ thread: { id: string }; created: boolean }>(
        "/api/student/chat/threads",
        {
          method: "POST",
          body: JSON.stringify({ peerUserId: c.other.userId })
        }
      );
      onOpened(r.thread.id);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
      setOpening(null);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        class="flex max-h-[80vh] w-full max-w-md flex-col rounded-lg bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <MessageCirclePlus className="size-4 text-primary" />
            Nova conversa
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="border-b border-border px-4 py-2">
          <div class="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={q}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)}
              placeholder="Buscar entre suas conexões…"
              aria-label="Buscar conexão"
              class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs outline-none focus:border-primary"
            />
          </div>
          <p class="mt-2 text-[10px] text-muted-foreground">
            Conversa só é possível com quem já é sua conexão aceita. Se já
            existir conversa com a pessoa, abrimos ela; senão, criamos uma nova.
          </p>
        </div>

        <ul class="flex-1 space-y-0.5 overflow-y-auto p-2">
          {phase === "loading" && (
            <li class="py-8 text-center text-xs text-muted-foreground">
              <Loader2 className="mr-1 inline size-3 animate-spin" /> Carregando…
            </li>
          )}
          {phase === "error" && (
            <li class="px-2 py-3 text-xs text-destructive">
              <AlertCircle className="mr-1 inline size-3" /> {err}
            </li>
          )}
          {phase === "ready" && filtered.length === 0 && (
            <li class="py-8 text-center text-xs text-muted-foreground">
              {q
                ? "Ninguém bate com essa busca."
                : "Você ainda não tem conexões aceitas. Vá em Conexões pra adicionar."}
            </li>
          )}
          {filtered.map((c) => {
            const initials = getInitials({
              name: c.other.name,
              socialName: null,
              initialsOverride: null
            });
            const isOpening = opening === c.other.userId;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => void startChat(c)}
                  disabled={isOpening}
                  class="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-accent disabled:opacity-50"
                >
                  <span class="grid size-9 shrink-0 place-items-center rounded-full bg-primary/15 text-[11px] font-bold uppercase text-primary">
                    {initials}
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-semibold">
                      {c.other.name}
                    </div>
                    {c.other.email && (
                      <div class="truncate text-[10px] text-muted-foreground">
                        {c.other.email}
                      </div>
                    )}
                  </div>
                  {isOpening && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ThreadItem({
  thread,
  active,
  onClick
}: {
  thread: ThreadRow;
  active: boolean;
  onClick: () => void;
}) {
  const initials = getInitials({
    name: thread.peer.name ?? thread.peer.email ?? "?",
    socialName: null,
    initialsOverride: null
  });
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        class={`flex w-full items-start gap-2 rounded-md px-2 py-2 text-left ${
          active
            ? "bg-primary/10 text-foreground"
            : "text-foreground hover:bg-accent"
        }`}
      >
        <span class="grid size-9 shrink-0 place-items-center rounded-full bg-primary/15 text-[11px] font-bold uppercase text-primary">
          {initials}
        </span>
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-1">
            <span class="truncate text-sm font-bold">
              {thread.peer.name ?? thread.peer.email ?? "Conversa"}
            </span>
            {thread.lastMessageAt && (
              <span class="shrink-0 text-[10px] text-muted-foreground">
                {formatRelative(thread.lastMessageAt)}
              </span>
            )}
          </div>
          <div class="flex items-center justify-between gap-1">
            <span class="line-clamp-1 text-[11px] text-muted-foreground">
              {thread.lastMessage?.isMine ? "Você: " : ""}
              {thread.lastMessage?.body ?? <em class="italic">sem mensagens ainda</em>}
            </span>
            {thread.unreadCount > 0 && (
              <span class="grid size-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                {thread.unreadCount}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function ChatPane({
  thread,
  onMessage
}: {
  thread: ThreadRow;
  onMessage: () => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatPhase, setChatPhase] = useState<"loading" | "ready" | "error">("loading");
  const [chatErr, setChatErr] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Polling + load.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const res = await call<{ messages: ChatMessage[] }>(
          `/api/student/chat/messages?threadId=${encodeURIComponent(thread.id)}&limit=100`
        );
        if (cancelled) return;
        const ordered = [...res.messages].reverse();
        setMessages(ordered);
        setChatPhase("ready");
        // Marca como lidas (sempre visível em hub view).
        const unread = ordered.some((m) => !m.isMine && !m.readByRecipientAt);
        if (unread) {
          void call(
            `/api/student/chat/messages?threadId=${encodeURIComponent(thread.id)}`,
            { method: "PATCH" }
          ).then(() => onMessage()).catch(() => {/* best-effort */});
        }
      } catch (e) {
        if (!cancelled) {
          setChatErr(friendlyError(e));
          setChatPhase("error");
        }
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [thread.id]);

  // Auto-scroll quando lista cresce.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (dist < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  async function send() {
    if (!input.trim() || sending) return;
    const body = input.trim();
    setSending(true);
    setChatErr(null);
    setInput("");
    try {
      const res = await call<{ message: ChatMessage }>(
        `/api/student/chat/messages?threadId=${encodeURIComponent(thread.id)}`,
        {
          method: "POST",
          body: JSON.stringify({ body })
        }
      );
      setMessages((prev) => [...prev, res.message]);
      onMessage();
    } catch (e) {
      // Surface o erro como banner no topo da lista de mensagens. Antes
      // setChatErr era setado mas o render só mostrava em phase==='error',
      // então o erro ficava invisível e o aluno achava que mandou ok.
      setChatErr(friendlyError(e));
      setInput(body);
    } finally {
      setSending(false);
    }
  }

  const initials = getInitials({
    name: thread.peer.name ?? thread.peer.email ?? "?",
    socialName: null,
    initialsOverride: null
  });

  return (
    <div class="flex h-full min-h-0 flex-col">
      <header class="flex shrink-0 items-center gap-3 border-b border-border bg-muted/20 px-4 py-3">
        <span class="grid size-9 place-items-center rounded-full bg-primary/15 text-[11px] font-bold uppercase text-primary">
          {initials}
        </span>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-bold">
            {thread.peer.name ?? thread.peer.email ?? "Conversa"}
          </div>
          {thread.peer.email && thread.peer.name && (
            <div class="truncate text-[11px] text-muted-foreground">
              {thread.peer.email}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() =>
            openChatWith(thread.peer.id, thread.peer.name ?? thread.peer.email ?? "Conversa")
          }
          title="Abrir nesta conversa numa janelinha lateral (como LinkedIn)"
          aria-label="Abrir em janelinha"
          class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <PictureInPicture2 className="size-4" />
        </button>
      </header>

      {chatErr && (
        <div class="mx-4 mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11px] text-destructive">
          <AlertCircle className="mt-0.5 size-3 shrink-0" />
          <span class="flex-1">{chatErr}</span>
          <button
            type="button"
            onClick={() => setChatErr(null)}
            aria-label="Fechar"
            class="text-destructive/70 hover:text-destructive"
          >
            <X className="size-3" />
          </button>
        </div>
      )}

      <div ref={scrollRef} class="flex-1 space-y-2 overflow-y-auto bg-background/40 p-4">
        {chatPhase === "loading" && (
          <p class="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> Carregando…
          </p>
        )}
        {chatPhase === "error" && !chatErr && (
          <p class="text-xs text-destructive">Não consegui carregar a conversa.</p>
        )}
        {chatPhase === "ready" && messages.length === 0 && (
          <p class="px-2 py-6 text-center text-xs text-muted-foreground">
            Diga oi — esta é a primeira conversa entre vocês.
          </p>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        class="flex shrink-0 items-end gap-2 border-t border-border bg-card px-3 py-3"
      >
        <textarea
          value={input}
          onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          rows={1}
          maxLength={4000}
          placeholder="Mensagem… (Enter envia, Shift+Enter quebra linha)"
          aria-label="Mensagem"
          class="max-h-32 flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label="Enviar"
          title="Enviar (Enter)"
          className="grid size-10 place-items-center rounded-md bg-primary text-primary-foreground hover:brightness-110 disabled:opacity-40"
        >
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </form>
    </div>
  );
}

// Toggle prominente "abrir nova mensagem em janelinha" — render bem visível
// no header do hub pra dar paridade de descoberta com o que existe em
// Preferências. [[project-peer-chat-autoopen-toggle]]
function AutoOpenToggle() {
  const p = prefs.value;
  const on = p.peerChatAutoOpen;
  return (
    <button
      type="button"
      onClick={() => savePrefs({ ...p, peerChatAutoOpen: !on })}
      title={
        on
          ? "Mensagens novas abrem em janelinha no rodapé. Clique para desligar."
          : "Mensagens novas só aparecem aqui. Clique para abrir em janelinha automaticamente."
      }
      aria-label={
        on
          ? "Abrir mensagens novas em janelinha (ligado, clique para desligar)"
          : "Abrir mensagens novas em janelinha (desligado, clique para ligar)"
      }
      class={`group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
        on
          ? "border-primary/50 bg-primary/10 text-primary hover:bg-primary/15"
          : "border-border bg-background text-muted-foreground hover:bg-accent"
      }`}
    >
      <PictureInPicture2 className="size-3.5" />
      <span class="hidden sm:inline">
        {on ? "Abrir em janelinha: ativado" : "Abrir em janelinha"}
      </span>
      <span class="sm:hidden">{on ? "Janelinha: on" : "Janelinha: off"}</span>
      <span
        class={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full border transition-colors ${
          on
            ? "border-primary bg-primary"
            : "border-border bg-muted"
        }`}
        aria-hidden
      >
        <span
          class={`absolute top-1/2 size-3 -translate-y-1/2 rounded-full transition-transform ${
            on ? "translate-x-[14px] bg-white" : "translate-x-[2px] bg-foreground/70 dark:bg-foreground/80"
          }`}
        />
      </span>
    </button>
  );
}

function Bubble({ m }: { m: ChatMessage }) {
  const share = parseShareBody(m.body);
  if (share) {
    return <ShareBubble payload={share} mine={m.isMine} />;
  }
  if (m.isMine) {
    return (
      <div class="flex justify-end">
        <div class="max-w-[70%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground">
          <pre class="whitespace-pre-wrap break-words font-sans">{m.body}</pre>
        </div>
      </div>
    );
  }
  return (
    <div class="flex justify-start">
      <div class="max-w-[70%] rounded-2xl rounded-bl-sm border border-border bg-card px-3.5 py-2 text-sm">
        <pre class="whitespace-pre-wrap break-words font-sans">{m.body}</pre>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}
